import { areObjectIdsEqual } from 'helpers/idComparison';
import { findDisplayedVersion } from 'shared/domain/documentation/documentationVersionHelpers';
import { downloadZipFromDirectory } from 'components/common/withSharedResourceDownload/downloadZip';
import { downloadFile } from 'services/fileOperations';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { debugLog } from 'shared/logger/debugLog';
import { DirectoryItem } from 'shared/types/documentation';
import { getISODateString } from 'shared/utils/date/dates';
import {
  DocumentationViewItem,
  isDirectoryItemType,
  isFileItem,
} from 'store/documentation/model/types';
import { ensureFileNameExtension, getDirectoryItems, getDisplayedVersionExtension } from './model';
import {
  DocumentationDownloadConfig,
  DocumentationDownloadDependencies,
  DocumentationDownloadService,
  DocumentationGalleryDownloadFile,
} from './types';

export function createDocumentationDownloadService(
  dependencies: DocumentationDownloadDependencies
): DocumentationDownloadService {
  const getVersionUrl = async (documentationId: string, versionId: string): Promise<string | null> => {
    try {
      const payload = await documentationClient.getSignedUrl({ documentationId, versionId });
      return payload.signedRequest;
    } catch (error) {
      debugLog(`Unable to get documentation URL for ${documentationId}: ${error}`);
      return null;
    }
  };

  /** The URL of the version the app shows for the documentation (its current one, PT-7117). */
  const getDocumentationUrl = async (documentation: DocumentationModel): Promise<string | null> => {
    const displayedVersion = findDisplayedVersion(documentation);
    if (!displayedVersion?._id) {
      debugLog(`Documentation ${documentation._id} has no version to show`);
      return null;
    }
    return getVersionUrl(documentation._id, displayedVersion._id);
  };

  /** Downloads the version the app shows (PT-7117: the current one, not the newest), from the gallery when it has it. */
  const downloadFileForItem = async (
    item: DocumentationViewItem,
    galleryFiles: DocumentationGalleryDownloadFile[]
  ): Promise<void> => {
    if (!isFileItem(item)) {
      return;
    }

    const displayedVersion = findDisplayedVersion(item);
    if (!displayedVersion?._id) {
      throw new Error('No version available for download');
    }
    const displayedVersionId = displayedVersion._id;

    const galleryFile = galleryFiles.find(
      (file) =>
        file.localId === item.localId &&
        areObjectIdsEqual(file.documentationId, item._id) &&
        areObjectIdsEqual(file.versionId, displayedVersionId)
    );

    const downloadUrl =
      galleryFile?.signedRequest?.signedRequest ||
      galleryFile?.downloadSrc ||
      (await getVersionUrl(item._id, displayedVersionId));

    if (!downloadUrl) {
      throw new Error('No download URL available');
    }

    const extension = getDisplayedVersionExtension(item);
    const fileName = ensureFileNameExtension(item.name, extension);

    await downloadFile(downloadUrl, fileName, extension);
  };

  const downloadDirectoryForItem = async (
    item: DocumentationViewItem,
    config: DocumentationDownloadConfig
  ): Promise<void> => {
    if (!isDirectoryItemType(item.type) || !item._id) {
      return;
    }

    const directoryItems = await getDirectoryItems(
      item._id,
      item.name,
      {
        ...config,
        getDirectories: dependencies.getDirectories,
        getDocumentations: dependencies.getDocumentations,
      },
      getDocumentationUrl
    );

    if (directoryItems.length === 0) {
      debugLog(`Directory ${item.name} is empty - nothing to download`);
      return;
    }

    downloadZipFromDirectory(
      directoryItems.map((directoryItem) => ({
        name: directoryItem.path,
        url: directoryItem.url,
      })),
      item.name
    );
  };

  const downloadSelectedAsZip = async (
    items: DocumentationViewItem[],
    config: DocumentationDownloadConfig
  ): Promise<void> => {
    const itemsToDownload: DirectoryItem[] = [];
    const singleFileItems: Array<{
      name: string;
      url: string;
      extension?: string;
    }> = [];

    for (const item of items) {
      if (isDirectoryItemType(item.type)) {
        const directoryItems = await getDirectoryItems(
          item._id!,
          item.name,
          {
            ...config,
            getDirectories: dependencies.getDirectories,
            getDocumentations: dependencies.getDocumentations,
          },
          getDocumentationUrl
        );
        itemsToDownload.push(...directoryItems);
      } else {
        const documentation = item as DocumentationModel;
        const url = await getDocumentationUrl(documentation);
        if (url) {
          const extension = getDisplayedVersionExtension(documentation);
          const fileName = ensureFileNameExtension(item.name, extension);

          itemsToDownload.push({
            name: fileName,
            path: fileName,
            url,
          });

          singleFileItems.push({
            name: fileName,
            url,
            extension,
          });
        }
      }
    }

    if (itemsToDownload.length === 0) {
      debugLog('No items to download');
      return;
    }

    if (
      singleFileItems.length === 1 &&
      itemsToDownload.length === 1 &&
      !isDirectoryItemType(items[0].type)
    ) {
      const item = singleFileItems[0];
      await downloadFile(item.url, item.name, item.extension);
      return;
    }

    const date = getISODateString(config.timezone);
    downloadZipFromDirectory(
      itemsToDownload.map((itemToDownload) => ({
        name: itemToDownload.path,
        url: itemToDownload.url,
      })),
      `hustro-download-${date}`
    );
  };

  return {
    downloadFileForItem,
    downloadDirectoryForItem,
    downloadSelectedAsZip,
    getDocumentationUrl,
  };
}

export type {
  DocumentationDownloadConfig,
  DocumentationDownloadDependencies,
  DocumentationDownloadService,
  DocumentationGalleryDownloadFile,
  DownloadItemsConfig,
  GetDirectories,
  GetDocumentations,
} from './types';
